import React, { useState, useEffect } from 'react';
import './AdminNewsManagement.css';
import Sidebar_admin from '../Sidebar_Admin/Sidebar_admin';
import axios from "../../../axiosInstance";

const AdminNewsManagement = () => {
  const [news, setNews] = useState([]); // Store news data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedNews, setSelectedNews] = useState(null); // Store the selected news for the modal
  const [statusFilter, setStatusFilter] = useState('approvalpending'); // Track the selected filter status
  const [searchQuery, setSearchQuery] = useState(''); // Store the search query for title/newsId search
  const [filteredNews, setFilteredNews] = useState([]); // Store the filtered news
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [newsPerPage] = useState(10); // Number of news articles per page

  // Fetch all news from API
  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/admin/news?status=${statusFilter}` // API endpoint with filters
      );
      setNews(response.data); // Set all fetched news
      setFilteredNews(response.data); // Initially, all news is filtered
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Failed to load news data');
      setLoading(false);
    }
  };

  // Initial fetch when the component mounts
  useEffect(() => {
    fetchNews();
  }, [statusFilter]); // Refetch whenever the filter changes

  // Handle search query change
  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    // Filter news based on `newsId` or `title`
    const filtered = news.filter(
      (newsItem) =>
        newsItem.newsId.toString().toLowerCase().includes(query) || // Match newsId
        newsItem.title.toLowerCase().includes(query) ||// Match title
        newsItem.reporterId.toString().toLowerCase().includes(query)
    );
    setFilteredNews(filtered); // Update filtered news
    setCurrentPage(1); // Reset to the first page
  };

  // Handle status filter change
  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus); // Update the status filter state
    setSearchQuery(''); // Reset search query
  };

  // Handle actions for publishing news
  const handlePublish = async (newsItemId) => {
    try {
      const response = await axios.put(`/api/admin/news/${newsItemId}/status`, {
        status: 'published', // The payload sent to the backend
      });

      if (response.status !== 200) {
        throw new Error('Failed to publish news');
      }

      fetchNews(); // Reload news data after the action
    } catch (err) {
      console.error(err);
      alert('Error publishing news');
    }
  };

  // Handle actions for rejecting news
  const handleReject = async (newsItemId) => {
    try {
      const response = await axios.put(`/api/admin/news/${newsItemId}/status`, {
        status: 'rejected', // The payload sent to the backend
      });

      if (response.status !== 200) {
        throw new Error('Failed to reject news');
      }

      fetchNews(); // Reload news data after the action
    } catch (err) {
      console.error(err);
      alert('Error rejecting news');
    }
  };

  // Pagination logic: Get current news based on current page
  const indexOfLastNews = currentPage * newsPerPage;
  const indexOfFirstNews = indexOfLastNews - newsPerPage;
  const currentNews = filteredNews.slice(indexOfFirstNews, indexOfLastNews);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) {
      console.log("Image path is missing!");
      return "/placeholder-image.jpg"; // Use a fallback image
    }
  
    const fullUrl = imagePath.startsWith("http")
      ? imagePath
      : `${process.env.REACT_APP_API_URL}/${imagePath}`; 
  
    console.log("Generated Image URL:", fullUrl); // Debugging log
  
    return fullUrl; // ✅ You MUST return the URL
  };
  
  return (
    <div>
      <Sidebar_admin />
      <div className="admin-panel">
        <h2 className="admin-panel__title">News Management</h2>

        {/* Search Box */}
        <div className="admin-panel__search">
          <input
            type="text"
            className="admin-panel__search-input"
            placeholder="Search by News ID or reporter id or  Title..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>

        {/* Filter buttons */}
        <div className="admin-panel__filters">
          <button
            className={`admin-panel__filter-button ${statusFilter === 'approvalpending' ? 'active' : ''}`}
            onClick={() => handleStatusChange('approvalpending')}
          >
            Pending
          </button>
          <button
            className={`admin-panel__filter-button ${statusFilter === 'published' ? 'active' : ''}`}
            onClick={() => handleStatusChange('published')}
          >
            Approved
          </button>
          <button
            className={`admin-panel__filter-button ${statusFilter === 'rejected' ? 'active' : ''}`}
            onClick={() => handleStatusChange('rejected')}
          >
            Rejected
          </button>
        </div>

        {loading ? (
          <p className="admin-panel__loading">Loading news...</p>
        ) : error ? (
          <p className="admin-panel__error">{error}</p>
        ) : (
          // Render filtered news
          Array.isArray(currentNews) && currentNews.length > 0 ? (
            <table className="admin-panel__table">
              <thead>
                <tr>
                  <th className="admin-panel__table-header">News ID</th>
                  <th className="admin-panel__table-header">Reporter ID</th>
                  <th className="admin-panel__table-header">Title</th>
                  <th className="admin-panel__table-header">District</th>
                  <th className="admin-panel__table-header">Status</th>
                  <th className="admin-panel__table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentNews.map((newsItem) => (
                  <tr key={newsItem._id} className="admin-panel__table-row">
                    <td>{newsItem.newsId}</td> {/* News ID */}
                    <td>{newsItem.reporterId}</td> {/* Reporter ID */}
                    <td>{newsItem.title}</td>
                    <td>{newsItem.district}</td>
                    <td>{newsItem.status}</td>
                    <td>
                      {newsItem.status === 'approvalpending' && (
                        <>
                          <button
                            className="admin-panel__button admin-panel__button--approve"
                            onClick={() => handlePublish(newsItem._id)}
                          >
                            <i className="fas fa-check"></i> {/* Publish Icon */}
                          </button>
                          <button
                            className="admin-panel__button admin-panel__button--reject"
                            onClick={() => handleReject(newsItem._id)}
                          >
                            <i className="fas fa-times"></i> {/* Reject Icon */}
                          </button>
                        </>
                      )}
                      {newsItem.status === 'published' && (
                        <button
                          className="admin-panel__button admin-panel__button--reject"
                          onClick={() => handleReject(newsItem._id)}
                        >
                          <i className="fas fa-times"></i> {/* Reject Icon */}
                        </button>
                      )}
                      {newsItem.status === 'rejected' && (
                        <>
                          <button
                            className="admin-panel__button admin-panel__button--approve"
                            onClick={() => handlePublish(newsItem._id)}
                          >
                            <i className="fas fa-check"></i> {/* Publish Icon */}
                          </button>
                        </>
                      )}
                      <button
                        className="admin-panel__button admin-panel__button--view"
                        onClick={() => setSelectedNews(newsItem)}
                      >
                        <i className="fas fa-eye"></i> {/* View Details Icon */}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No news available</p>
          )
        )}

        {/* Pagination Controls */}
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          <span>{currentPage}</span>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage * newsPerPage >= filteredNews.length}
          >
            Next
          </button>
        </div>

        {/* Modal for displaying news details */}
        {selectedNews && (
          <div className="modal-content">
            <span className="modal-close" onClick={() => setSelectedNews(null)}>&times;</span>
            <h3>News Details</h3>
            <div className="news-details">
              <p><strong>Title:</strong> {selectedNews.title}</p>
              <p><strong>Short Description:</strong> {selectedNews.shortDescription}</p>
              <p><strong>Description:</strong> {selectedNews.description}</p>
              <p><strong>District:</strong> {selectedNews.district}</p>
              <p><strong>Submitted At:</strong> {new Date(selectedNews.createdAt).toLocaleString()}</p>
              <p><strong>Status:</strong> {selectedNews.status}</p>
              {selectedNews.image && (
                <div>
                  <img src={getFullImageUrl(selectedNews.image)} alt="News" className="ad-news-image" />

                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNewsManagement;
